import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, ChevronDown, Menu, X, ArrowRight, Zap } from "lucide-react";
import { navLinks, company } from "../data/mock";
import { useQuote } from "../context/QuoteContext";
import { OfferBar } from "./OfferBar";

const Logo = () => (
  <Link to="/" className="flex items-center shrink-0">
    <img
      src={company.logo}
      alt="Set Up Home Theater"
      className="h-14 w-auto object-contain"
    />
  </Link>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(null);
  const { openQuote } = useQuote();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <>
    {location.pathname !== "/" && <OfferBar />}
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur shadow-sm" : "bg-white"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-[84px]">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(link.label)}
                  onMouseLeave={() => setServicesOpen(null)}
                >
                  <button className="flex items-center gap-1 text-[15px] font-medium text-[#0a1f44] hover:text-[#3b9dff] transition-colors whitespace-nowrap">
                    {link.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 w-64 transition-all duration-200 ${
                      servicesOpen === link.label
                        ? "opacity-100 visible translate-y-0"
                        : "opacity-0 invisible -translate-y-1"
                    }`}
                  >
                    <div className="bg-white rounded-xl shadow-xl border border-slate-100 p-2 max-h-[70vh] overflow-y-auto">
                      {link.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block px-4 py-2.5 rounded-lg text-sm text-[#0a1f44] hover:bg-[#eef4ff] hover:text-[#3b9dff] transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : link.external ? (
                <a
                  key={link.label}
                  href={link.to}
                  className="text-[15px] font-medium text-[#0a1f44] hover:text-[#3b9dff] transition-colors whitespace-nowrap"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-[15px] font-medium transition-colors hover:text-[#3b9dff] whitespace-nowrap ${
                    location.pathname === link.to
                      ? "text-[#3b9dff]"
                      : "text-[#0a1f44]"
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3 ml-5 xl:ml-8">
            <a
              href={company.phoneLink}
              data-testid="header-call-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#3b9dff]/40 text-[#3b9dff] font-semibold text-sm hover:bg-[#eef4ff] transition-colors whitespace-nowrap"
            >
              <Phone className="w-4 h-4" />
              Call or text {company.phone}
            </a>
            {location.pathname === "/" && (
              <Link to="/tv-mounting-quote" data-testid="header-instant-quote-btn" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0a1f44] text-white font-semibold text-sm hover:bg-[#123a7a] transition-colors whitespace-nowrap shrink-0">
                <Zap className="w-4 h-4 text-[#3b9dff]" /> Instant Quote
              </Link>
            )}
            <button
              onClick={() => openQuote()}
              data-testid="header-get-quote-btn"
              className={`${location.pathname === "/" ? "hidden 2xl:flex" : "flex"} items-center gap-2 px-5 py-2.5 rounded-full bg-[#3b9dff] text-white font-semibold text-sm hover:bg-[#2b8ae8] transition-colors shadow-sm whitespace-nowrap shrink-0`}
            >
              Get a Quote <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden flex items-center gap-2 bg-[#0a1f44] text-white font-bold text-sm rounded-full pl-4 pr-5 py-2.5 shadow-md active:scale-95 transition-transform"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" strokeWidth={2.75} />}
            <span>{mobileOpen ? "Close" : "Menu"}</span>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-5 py-3 shadow-lg max-h-[calc(100vh-140px)] overflow-y-auto pb-28" data-testid="mobile-menu">
          {navLinks.map((link) => (
            <div key={link.label} className="border-b border-slate-100 last:border-0">
              {link.external ? (
                <a
                  href={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between py-4 text-base font-semibold text-[#0a1f44]"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center justify-between py-4 text-base font-semibold ${location.pathname === link.to ? "text-[#3b9dff]" : "text-[#0a1f44]"}`}
                >
                  {link.label}
                </Link>
              )}
              {link.children && (
                <div className="pl-4 border-l-2 border-[#3b9dff]/20 ml-1 pb-2">
                  {link.children.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      onClick={() => setMobileOpen(false)}
                      className="block py-2.5 text-[15px] text-slate-500"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="pt-4 flex flex-col gap-2.5">
            <a
              href={company.phoneLink}
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-call-btn"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#3b9dff]/40 text-[#3b9dff] font-semibold text-base"
            >
              <Phone className="w-4 h-4" /> Call or text {company.phone}
            </a>
            {location.pathname === "/" && (
              <Link to="/tv-mounting-quote" onClick={() => setMobileOpen(false)} data-testid="mobile-instant-quote-btn" className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#0a1f44] text-white font-semibold text-base">
                <Zap className="w-4 h-4 text-[#3b9dff]" /> Instant Quote
              </Link>
            )}
            <button
              onClick={() => { setMobileOpen(false); openQuote(); }}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#3b9dff] text-white font-semibold text-base"
            >
              Get a Quote <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </header>
    </>
  );
};

export default Header;
